import { buildWorkflowPayload, assignmentsSignature } from "../workflow/workflowAssignments";
import { isPastDate } from "./bookingStepperUtils";

// What the booking form sends to create_ezp_booking / create_partner_booking, and the
// edit screen's "did anything change" check. Both moved unchanged out of Bookings.jsx's
// handleSubmit so they can be tested.

export const normalizeSpecifics = (s) => ({
  asset: String(s?.customer_asset_id ?? ""),
  complaint: s?.customer_complaint || "",
  action: s?.action_taken || ""
});

// Service Specifics diff against what was loaded for the booking (an unreported baseline
// counts as empty). A staged new-asset draft is always a pending change.
export function specificsChanged(baseline, serviceSpecifics) {
  const before = baseline || normalizeSpecifics(null);
  const now = normalizeSpecifics(serviceSpecifics);
  return (
    now.asset !== before.asset ||
    now.complaint !== before.complaint ||
    now.action !== before.action ||
    !!serviceSpecifics?.new_asset_draft
  );
}

const locationOf = (values) => ({
  house_no: values?.house_no || "",
  area: values?.area || "",
  location: values?.location || "",
  state: values?.state || "",
  landmark: values?.landmark || "",
  lat: values?.lat || "",
  lng: values?.lng || ""
});

// "HH:MM" of a time as the screen holds it ("09:00", "09:00:00", "2026-10-12T09:00:00" or empty).
export const hhmm = (value) => String(value || "").split("T").pop().slice(0, 5);

// The booking's time as "HH:MM".
//   service step Required (today): the first selected provider slot, else the stored time;
//   service step Optional (assign the provider later): the time chosen in the time list - a provider
//   picked on the calendar sets that time too, so the two never disagree.
export const slotTime = (providerSelected, timeSelect, providerOptional = false) =>
  providerOptional ? hhmm(timeSelect) : (providerSelected?.[0]?.start?.split("T")?.[1] || timeSelect || "").slice(0, 5);

// Who is on the booking, as a stable comparable string ("3,4"): ids only, so a provider found on
// the calendar and one restored from the saved booking compare the same.
export const providerIdsKey = (providerSelected) =>
  [...new Set((providerSelected || []).map((p) => Number(p?.extendedProps?.provider_id)).filter((n) => Number.isFinite(n) && n > 0))]
    .sort((a, b) => a - b)
    .join(",");

export function buildSubmitValues({ values, ctx }) {
  const out = { ...values };
  const providers = (ctx.providerSelected || []).map((ele) => ele?.extendedProps?.provider_id);

  out.existingUserId = ctx.user_id;
  out.editable = ctx.editable ? 1 : 0;
  out.subscription_id = ctx.selectedSubscription
    ? ctx.selectedSubscription.user_subscription_id
    : ctx.existingSubscriptionId || null;

  out.date = ctx.dateSelect;
  // A backdated booking is a record of work already done: the server saves it as usual but sends
  // no SMS / crew WhatsApp / push. Only this form sends the flag, so other clients are unaffected.
  if (isPastDate(ctx.dateSelect)) out.skip_notifications = true;
  out.isLocationChanged = JSON.stringify(locationOf(values)) !== JSON.stringify(ctx.firstLocation);
  if (ctx.providerOptional) {
    // The time is the one chosen in the time list (the screen refuses to continue without it).
    out.time = hhmm(ctx.timeSelect);
  } else {
    const t1Parts = ctx.providerSelected?.[0]?.start?.split("T")?.[1]?.split(":");
    out.time = t1Parts ? `${t1Parts[0]}:${t1Parts[1]}` : ctx.timeSelect || "00:00";
  }
  out.serviceData = ctx.serviceData;
  out.priceSection = ctx.priceSection;
  out.providers = providers.join(", ");
  // Only when the field is shown. "" clears; omitting the key leaves the booking's
  // resource untouched on the server.
  if (ctx.showResourceField) out.resource_ids = ctx.resourceIds;
  // Only for a service that has a workflow; null for a step left empty so a person who
  // was taken off really is removed.
  if (ctx.formWorkflow) {
    out.workflow = buildWorkflowPayload(ctx.formWorkflow, ctx.workflowAssign);
  }
  // Snapshot of the crew selection so a return from online payment can restore it.
  out.providerSelectedData = (ctx.providerSelected || []).map((ev) => ({
    provider_id: ev?.extendedProps?.provider_id,
    start: ev?.start instanceof Date ? ev.start.toISOString() : String(ev?.start || ""),
    end: ev?.end instanceof Date ? ev.end.toISOString() : String(ev?.end || ""),
    title: ev?.title || ""
  }));
  out.group_id = ctx.group_id;
  // An edit says which providers it loaded, so the server changes the provider only if THIS edit did
  // (an untouched list keeps whoever was assigned meanwhile, e.g. from the dashboard).
  if (ctx.providersBaseline !== undefined && ctx.providersBaseline !== null) out.providers_baseline = ctx.providersBaseline;
  // Lets the booking flag the source estimation as converted once it exists.
  if (ctx.fromEstimationId) out.from_estimation = ctx.fromEstimationId;
  out.additional_notes = ctx.additionalNotes;
  out.paid_amount = ctx.paidAmount;
  out.currency = ctx.currency;
  return out;
}

// What each service on the booking costs, as a comparable string ("11:50,12:30.5"). Rows are
// { service_offering_id, <amountKey> }; the first row of an offering wins (as when the screen
// seeds an amount from the saved booking). Lets the edit screen see a price-only change.
export const amountsSignature = (rows, amountKey = "amount") => {
  const seen = new Set();
  const parts = [];
  for (const r of rows || []) {
    const id = r?.service_offering_id;
    if (id === undefined || id === null || seen.has(id)) continue;
    seen.add(id);
    parts.push(`${id}:${Number(r?.[amountKey]) || 0}`);
  }
  return parts.sort().join(",");
};

// Each service line's own discount, from priceSection's service rows (same shape as amounts).
export const lineDiscountsSignature = (priceSection) =>
  amountsSignature(
    (priceSection || []).filter((r) => r?.service_offering_id && !r.isCustom && !r.isPart),
    "line_discount"
  );

// Custom line items ("name::amount||...") as a comparable string; untouched blank rows are ignored.
export const customItemsSignature = (rows) =>
  (rows || [])
    .filter((r) => r?.isCustom && ((r.name || "").trim() || Number(r.amount)))
    .map((r) => `${(r.name || "").trim()}:${Number(r.amount) || 0}`)
    .join("|");

// Edit screen: true when nothing the vendor could have touched differs from what loaded.
export function isBookingUnchanged({ values, snap, ctx }) {
  const currentOfferingIds = (ctx.serviceData || [])
    .filter((d) => d.added)
    .map((d) => d.service_offering_id)
    .sort()
    .join(",");
  const currentProvidersStr = providerIdsKey(ctx.providerSelected);
  const currentTime = slotTime(ctx.providerSelected, ctx.timeSelect, ctx.providerOptional);

  return (
    (values.userName || "") === snap.user_name &&
    String(values.phoneNumber || "") === snap.phone_number &&
    (values.email || "") === snap.email &&
    (values.house_no || "") === snap.house_no &&
    (values.area || "") === snap.area &&
    (values.location || "") === snap.location &&
    (values.state || "") === snap.state &&
    (values.landmark || "") === snap.landmark &&
    String(values.lat || "") === snap.lat &&
    String(values.lng || "") === snap.lng &&
    ctx.dateSelect === snap.service_date &&
    currentTime === snap.service_time &&
    (ctx.additionalNotes || "") === snap.additional_info &&
    Number(ctx.discount || 0) === snap.discount &&
    Number(ctx.paidAmount || 0) === snap.total_paid_amount &&
    Number(values.status || 0) === snap.status &&
    Number(values.payment || 0) === snap.payment &&
    currentOfferingIds === snap.offering_ids &&
    amountsSignature((ctx.serviceData || []).filter((d) => d.added)) === snap.amounts &&
    (snap.line_discounts === undefined || lineDiscountsSignature(ctx.priceSection) === snap.line_discounts) &&
    customItemsSignature(ctx.priceSection) === snap.custom_items &&
    currentProvidersStr === snap.providers &&
    !ctx.specificsChanged &&
    (!ctx.showResourceField || (ctx.resourceIds || "") === (snap.resource_ids || "")) &&
    (!ctx.formWorkflow ||
      assignmentsSignature(ctx.formWorkflow, ctx.workflowAssign) === ctx.workflowInitialSignature)
  );
}
